import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarService } from '@/services/calendar.service';
import { CreateCalendarData, UpdateCalendarData, CalendarFilters } from '@/types/calendar';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';

/**
 * Calendar Hooks
 *
 * React Query hooks for calendar management
 */

/**
 * Fetch all calendars with optional filters
 * @param options - filters and/or enabled flag
 */
export const useCalendars = (options?: CalendarFilters & { enabled?: boolean }) => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const { enabled, ...filters } = options || {};
  const hasFilters = Object.keys(filters).length > 0;

  return useQuery({
    queryKey: hasFilters
      ? [...teamQueryKeys.calendars.all(teamId), 'list', filters]
      : teamQueryKeys.calendars.all(teamId),
    queryFn: () => calendarService.getCalendars(hasFilters ? filters : undefined),
    enabled: hasTeam && (enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes - calendars rarely change
  });
};

/**
 * Fetch single calendar by ID
 */
export const useCalendar = (id: number) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.calendars.detail(teamId, id),
    queryFn: () => calendarService.getCalendar(id),
    enabled: hasTeam && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes - calendars rarely change
  });
};

/**
 * Fetch default calendar (auto-creates if doesn't exist)
 */
export const useDefaultCalendar = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.calendars.default(teamId),
    queryFn: calendarService.getDefaultCalendar,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000, // 5 minutes - calendars rarely change
  });
};

/**
 * Create new calendar
 */
export const useCreateCalendar = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (data: CreateCalendarData) => calendarService.createCalendar(data),
    onSuccess: () => {
      // Invalidate all calendar queries to refetch
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.all(teamId) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.default(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Update calendar
 */
export const useUpdateCalendar = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCalendarData }) =>
      calendarService.updateCalendar(id, data),
    onSuccess: (_, variables) => {
      // Invalidate calendar queries
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.all(teamId) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.detail(teamId, variables.id) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.default(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Delete calendar
 */
export const useDeleteCalendar = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => calendarService.deleteCalendar(id),
    onSuccess: () => {
      // Invalidate calendar queries
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.all(teamId) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendars.default(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};
